import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { debounceTime, EMPTY, filter, map, of, switchMap, tap } from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { selectUser } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { ToastService } from '@ps/core-overlay';
import {
  SURVEY_ACTIVITY_LOG_MESSAGES,
  SurveyMetadataForm,
  surveyToAdvancedTargetingDefinitions,
} from '@ps/survey-utils';
import {
  UserSettingsActionsFacade,
  UserSettingsSelectorsFacade,
} from '@ps/user-settings-data-access';
import {
  SurveyActivityLogActions,
  SurveyService,
} from '@ps/survey-data-access';

import { SurveyScoreService } from '../../services/survey-score/survey-score.service';
import { FeasibilityService } from '../../services/feasibility/feasibility.service';
import { SurveyProcessingService } from '../../services/survey-processing/survey-processing.service';
import { isEligibleForStandardSurvey } from '../../utils/survey-creation-eligibility';
import {
  SurveyCreationActions,
  SurveyCreationApiActions,
  SurveyCreationNavbarActions,
  SurveyCreationNavbarApiActions,
} from './survey-creation-audience.actions';
import {
  selectAdvancedTargetingDefinitions,
  selectHasHydratedAdvancedTargeting,
  selectLocationSelectionsByQualificationCode,
  selectSurvey,
  selectSurveyId,
} from './survey-creation-audience.selectors';
import { SurveyCreationReferenceApiActions } from '../reference/survey-creation-reference.actions';
import { selectQualifications } from '../reference/survey-creation-reference.selectors';

@Injectable()
export class SurveyCreationAudienceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);

  private readonly _surveyService = inject(SurveyService);
  private readonly _surveyScoreService = inject(SurveyScoreService);
  private readonly _surveyProcessingService = inject(SurveyProcessingService);
  private readonly _feasibilityService = inject(FeasibilityService);

  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _userSettingsActions = inject(UserSettingsActionsFacade);

  public readonly createStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.createStandardSurvey),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      // Don't create a standard survey if one already exists
      filter(
        ([{ metadata }, surveyId]) =>
          !surveyId && isEligibleForStandardSurvey(metadata),
      ),
      map(([{ metadata }]) => metadata),
      concatLatestFrom(() => [
        this._store.select(selectUser),
        this._store.select(selectAdvancedTargetingDefinitions),
        this._store.select(selectLocationSelectionsByQualificationCode),
        this._store.select(selectQualifications),
      ]),
      map(
        ([
          metadata,
          user,
          definitions,
          locationSelectionsByQualificationCode,
          qualifications,
        ]) => ({
          metadata,
          user: Option.from(user).unwrap(),
          targeting: {
            definitions,
            locationSelectionsByQualificationCode,
            qualifications,
          },
        }),
      ),
      switchMap(({ metadata, user, targeting }) =>
        this._surveyService
          .createStandardSurvey(metadata, user, targeting)
          .pipe(
            map((survey) => {
              this._toastService.success('Survey successfully saved', {
                keepOpen: false,
              });
              return SurveyCreationApiActions.createStandardSurveySuccess({
                survey,
              });
            }),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationApiActions.createStandardSurveyFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  // The create page hands the survey off to the edit route as soon as it exists.
  // Safe now that the edit page renders the Advanced Targeting / Set Location
  // block and hydrates a saved survey's qualifications — the reason this was
  // held back while the creation route stood alone.
  public readonly createStandardSurveyNavigate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationApiActions.createStandardSurveySuccess),
        tap(({ survey }) => {
          this._router.navigate([APP_ROUTES.editSurvey(survey.id)]);
        }),
      ),
    { dispatch: false },
  );

  public readonly updateStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.updateSurvey),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      map(([{ metadata }, surveyId]) => ({ metadata, surveyId })),
      filter(
        (data): data is { metadata: SurveyMetadataForm; surveyId: number } =>
          !!data.surveyId && isEligibleForStandardSurvey(data.metadata),
      ),
      concatLatestFrom(() => [
        this._store.select(selectUser),
        this._store.select(selectAdvancedTargetingDefinitions),
        this._store.select(selectLocationSelectionsByQualificationCode),
        this._store.select(selectQualifications),
      ]),
      map(
        ([
          { metadata, surveyId },
          user,
          definitions,
          locationSelectionsByQualificationCode,
          qualifications,
        ]) => ({
          metadata,
          surveyId,
          user: Option.from(user),
          // The PUT replaces qualifications wholesale, so a metadata-only body
          // would delete every applied definition and location selection.
          targeting: {
            definitions,
            locationSelectionsByQualificationCode,
            qualifications,
          },
        }),
      ),
      filter(({ user }) => user.isSome()),
      map(({ metadata, surveyId, user, targeting }) => ({
        metadata,
        surveyId,
        user: user.unwrap(),
        targeting,
      })),
      switchMap(({ metadata, surveyId, user, targeting }) =>
        this._surveyService
          .updateStandardSurvey(surveyId, metadata, user, targeting)
          .pipe(
            map((survey) => {
              this._toastService.success('Survey successfully saved', {
                keepOpen: false,
              });
              return SurveyCreationApiActions.updateStandardSurveySuccess({
                survey,
              });
            }),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationApiActions.updateStandardSurveyFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly loadSurveyForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.init),
      filter(
        (action): action is typeof action & { surveyId: number } =>
          !!action.surveyId,
      ),
      concatLatestFrom(() => this._store.select(selectSurvey)),
      // Skip the fetch when we already hold this exact survey in memory —
      // e.g. right after create → edit, where the create response already
      // has everything loadSurveyForEdit would otherwise re-fetch.
      filter(([{ surveyId }, survey]) => !survey || survey.id !== surveyId),
      switchMap(([{ surveyId }]) =>
        this._surveyService.getById(surveyId, 1).pipe(
          map((survey) =>
            SurveyCreationApiActions.loadSurveyForEditSuccess({ survey }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationApiActions.loadSurveyForEditFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Hydration needs the survey AND the reference qualification list (a code that
  // also exists as a front-view/location qualification is not Advanced
  // Targeting), and their arrival order varies: the reference list is fetched
  // only once the survey's locale is known, but a warm TTL cache means
  // loadQualificationsSuccess may never fire at all. Listening to both makes
  // whichever lands second complete the hydration.
  public readonly hydrateAdvancedTargeting$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationApiActions.loadSurveyForEditSuccess,
        SurveyCreationReferenceApiActions.loadQualificationsSuccess,
      ),
      concatLatestFrom(() => [
        this._store.select(selectSurvey),
        this._store.select(selectQualifications),
        this._store.select(selectHasHydratedAdvancedTargeting),
      ]),
      filter(
        ([, survey, qualifications, hasHydrated]) =>
          !hasHydrated && !!survey && qualifications.length > 0,
      ),
      map(([, survey, qualifications]) =>
        SurveyCreationActions.hydrateAdvancedTargetingDefinitions({
          definitions: surveyToAdvancedTargetingDefinitions(
            survey?.qualifications,
            qualifications,
            survey?.quotas,
          ),
        }),
      ),
    ),
  );

  public readonly computeScore$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationNavbarActions.computeScore),
      switchMap(({ surveyId, cpi, ir, loi }) =>
        this._surveyScoreService.computeScore(surveyId, { cpi, ir, loi }).pipe(
          map(({ score, scorePercentage }) =>
            SurveyCreationNavbarApiActions.computeScoreSuccess({
              score,
              scorePercentage,
            }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationNavbarApiActions.computeScoreFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly toggleManualProcessing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationNavbarActions.toggleManualProcessing),
      filter(() =>
        this._userSettingsSelectors.buyerConfigSettingsId().isSome(),
      ),
      switchMap(({ companyId, enabled }) => {
        const buyerConfigSettingsId = this._userSettingsSelectors
          .buyerConfigSettingsId()
          .unwrap();

        return this._surveyProcessingService
          .updateManualProcessing(buyerConfigSettingsId, companyId, enabled)
          .pipe(
            tap(() => {
              this._toastService.success(
                'Processing setting updated successfully',
              );
            }),
            tap(() => {
              this._userSettingsActions.updateBuyerSetting(
                'enableManualProcessing',
                companyId,
                enabled,
              );
            }),
            switchMap(() => {
              const logAction = SurveyActivityLogActions.logOperatorActivity({
                log: {
                  message: SURVEY_ACTIVITY_LOG_MESSAGES.manualProcessingToggled,
                  metadata: { action: enabled ? 'ON' : 'OFF' },
                },
              });

              // Switching back to Real Time fires one catch-up recalc, since
              // manual mode may have held changes back while it was on.
              return enabled
                ? of(logAction)
                : of(
                    logAction,
                    SurveyCreationNavbarActions.recalculateFeasibility(),
                  );
            }),
            this.psCatchError(() => EMPTY, { notify: true }),
          );
      }),
    ),
  );

  public readonly recalculateFeasibility$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationNavbarActions.recalculateFeasibility,
        SurveyCreationApiActions.loadSurveyForEditSuccess,
        SurveyCreationApiActions.createStandardSurveySuccess,
      ),
      debounceTime(500),
      concatLatestFrom(() => [
        this._store.select(selectSurvey),
        this._store.select(selectUser),
      ]),
      map(([, survey, user]) => ({
        survey: Option.from(survey).unwrap(),
        user: Option.from(user).unwrap(),
      })),
      switchMap(({ survey, user }) =>
        this._feasibilityService.compute(survey, user).pipe(
          map(({ total, quotaGroups, audience }) =>
            SurveyCreationNavbarApiActions.recalculateFeasibilitySuccess({
              total,
              quotaGroups,
              audience,
            }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationNavbarApiActions.recalculateFeasibilityFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
